/**
 * AP-34 Nachtrag 2, 3.4 — **Gemeinkosten**: was der Betrieb ohne Projektbezug ausgibt, je Zeile
 * einer Eingangsrechnung. Liste im Listen-Standard, dazu die Gruppierung nach Kategorie oder Monat
 * mit Zwischensummen. Kosten ist, was freigegeben oder übergeben ist — Rechnungen im Freigabelauf
 * stehen dabei, zählen aber in keiner Summe.
 */
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowDown, ArrowUp, ArrowUpDown, Check, Settings } from 'lucide-react'
import { geldText } from '@symphenia/shared-types'
import { FilterBar, filterAusAdresse } from '@/components/list/FilterBar'
import type { ActiveFilter, FilterFieldDef } from '@/components/list/list.types'
import { ExportButton } from '@/components/ui/ExportButton'
import { HelpPopover } from '@/components/ui/HelpPopover'
import { LayoutDefaultActions } from '@/components/ui/LayoutDefaultActions'
import { apiErrorText } from '@/lib/api-error'
import { pageWide } from '@/lib/form-styles'
import { loadUiSetting, markUiSettingApplied, saveUiSetting } from '@/lib/ui-settings'
import { fetchIncomingFrame, fetchOverhead, overheadExportRequest } from '../api/incoming-invoices.api'
import { tagText } from '../lib/texte'
import type { IncomingFrame, OverheadLine, OverheadParams, OverheadReport } from '../types/incoming-invoices.types'

interface ColDef {
  key: keyof OverheadLine | 'state'
  label: string
  align?: 'right'
}
const SPALTEN: ColDef[] = [
  { key: 'invoiceDate', label: 'Rechnungsdatum' },
  { key: 'supplierName', label: 'Lieferant' },
  { key: 'categoryName', label: 'Kategorie' },
  { key: 'name', label: 'Bezeichnung' },
  { key: 'amount', label: 'Betrag', align: 'right' },
  { key: 'expenseAccount', label: 'Sachkonto' },
  { key: 'state', label: 'Stand' },
]
const VORGABE_SPALTEN = ['invoiceDate', 'supplierName', 'categoryName', 'name', 'amount']
const LAYOUT = 'columnbar_layout_incoming_overhead'
type Gruppierung = 'none' | 'category' | 'month'
const GRUPPEN: [Gruppierung, string][] = [
  ['category', 'nach Kategorie'],
  ['month', 'nach Monat'],
  ['none', 'ohne Gruppierung'],
]

interface ColumnState {
  columnKeys: string[]
  columnWidths: Record<string, number>
  sortKey: string
  sortDir: 'asc' | 'desc'
  group: Gruppierung
}
const vorgabe = (): ColumnState => ({ columnKeys: VORGABE_SPALTEN, columnWidths: {}, sortKey: 'invoiceDate', sortDir: 'desc', group: 'category' })
const ladeSpalten = (): ColumnState => {
  const g = loadUiSetting<Partial<ColumnState> | null>(LAYOUT, null)
  return g?.columnKeys?.length ? { ...vorgabe(), ...g, columnKeys: g.columnKeys } : vorgabe()
}

const filterFelder = (rahmen: IncomingFrame | null): FilterFieldDef[] => [
  { key: 'search', label: 'Suche', type: 'text', category: 'core', operators: ['contains'] },
  { key: 'from', label: 'Rechnungsdatum ab', type: 'date', category: 'core', operators: ['is'] },
  { key: 'to', label: 'Rechnungsdatum bis', type: 'date', category: 'core', operators: ['is'] },
  { key: 'categoryId', label: 'Kategorie', type: 'select', category: 'core', operators: ['is'], options: (rahmen?.overheadCategories ?? []).map((k) => ({ value: k.id, label: k.name })) },
]
function filterParams(filters: Record<string, ActiveFilter>): OverheadParams {
  const p: OverheadParams = {}
  for (const [key, a] of Object.entries(filters)) {
    if (!a.value) continue
    if (key === 'search') p.search = a.value
    if (key === 'from') p.from = a.value.slice(0, 10)
    if (key === 'to') p.to = a.value.slice(0, 10)
    if (key === 'categoryId') p.categoryId = a.value
  }
  return p
}
const filterText = (filters: Record<string, ActiveFilter>, rahmen: IncomingFrame | null) => {
  const p = filterParams(filters)
  return [p.from && `ab ${tagText(p.from)}`, p.to && `bis ${tagText(p.to)}`, p.categoryId && rahmen?.overheadCategories.find((k) => k.id === p.categoryId)?.name, p.search && `„${p.search}"`].filter(Boolean).join(' · ')
}
const monatText = (m: string | null) => (m ? `${m.slice(5, 7)}/${m.slice(0, 4)}` : 'ohne Datum')

export default function IncomingOverheadPage() {
  const navigate = useNavigate()
  const [rahmen, setRahmen] = useState<IncomingFrame | null>(null)
  useEffect(() => {
    fetchIncomingFrame().then(setRahmen).catch(() => setRahmen(null))
  }, [])
  const filterFields = useMemo(() => filterFelder(rahmen), [rahmen])
  // AP-40 6.4: Filter aus der Adresse (Kachel des Dashboards) — einmal beim Öffnen gelesen.
  const [suchParameter] = useSearchParams()
  const [adresse] = useState(() => filterAusAdresse(suchParameter, filterFelder(null)))

  const [filterValues, setFilterValues] = useState<Record<string, ActiveFilter>>(adresse ?? {})
  const [colState, setColState] = useState<ColumnState>(ladeSpalten)
  const [columnConfigMode, setColumnConfigMode] = useState(false)
  const [bericht, setBericht] = useState<OverheadReport | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const dragKeyRef = useRef<string | null>(null)
  const [dragOverKey, setDragOverKey] = useState<string | null>(null)

  const fetchData = useCallback(async (filters: Record<string, ActiveFilter>) => {
    setLoading(true)
    setError(null)
    try {
      setBericht(await fetchOverhead(filterParams(filters)))
    } catch (e) {
      setError(apiErrorText(e, 'Die Gemeinkosten ließen sich nicht laden.'))
    } finally {
      setLoading(false)
    }
  }, [])
  useEffect(() => {
    void fetchData(adresse ?? {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchData])

  const handleFiltersChange = useCallback(
    (neu: Record<string, ActiveFilter>) => {
      setFilterValues(neu)
      if (debounceTimer.current) clearTimeout(debounceTimer.current)
      debounceTimer.current = setTimeout(() => void fetchData(neu), 400)
    },
    [fetchData],
  )
  const updateColState = useCallback((updater: (prev: ColumnState) => ColumnState) => {
    setColState((prev) => {
      const next = updater(prev)
      saveUiSetting(LAYOUT, next)
      return next
    })
  }, [])
  // Sortiert wird in der Seite — die Auswertung kommt als Ganzes, kein weiterer Abruf.
  const handleSort = (key: string) => updateColState((prev) => ({ ...prev, sortKey: key, sortDir: prev.sortKey === key && prev.sortDir === 'asc' ? 'desc' : 'asc' }))
  const handleDrop = (ziel: string) => {
    const quelle = dragKeyRef.current
    setDragOverKey(null)
    if (!quelle || quelle === ziel) return
    updateColState((prev) => {
      const keys = [...prev.columnKeys]
      const von = keys.indexOf(quelle)
      const nach = keys.indexOf(ziel)
      if (von < 0 || nach < 0) return prev
      keys.splice(von, 1)
      keys.splice(nach, 0, quelle)
      return { ...prev, columnKeys: keys }
    })
  }
  const handleResizeStart = (e: React.MouseEvent, key: string) => {
    e.preventDefault()
    const startW = (e.target as HTMLElement).closest('th')?.getBoundingClientRect().width ?? 120
    const startX = e.clientX
    const onMove = (ev: MouseEvent) => updateColState((prev) => ({ ...prev, columnWidths: { ...prev.columnWidths, [key]: Math.max(60, startW + ev.clientX - startX) } }))
    const onUp = () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }

  const shownColumns = colState.columnKeys.map((k) => SPALTEN.find((c) => c.key === k)).filter((c): c is ColDef => !!c)
  const availableToAdd = SPALTEN.filter((c) => !colState.columnKeys.includes(c.key))
  const totalCols = 1 + shownColumns.length + (columnConfigMode && availableToAdd.length ? 1 : 0) + 1

  const gruppen = useMemo(() => {
    const wert = (z: OverheadLine, key: string): string | number => (key === 'amount' ? z.amount : key === 'state' ? (z.inApproval ? 1 : 0) : String((z as unknown as Record<string, unknown>)[key] ?? ''))
    const zeilen = [...(bericht?.lines ?? [])].sort((a, b) => {
      const x = wert(a, colState.sortKey)
      const y = wert(b, colState.sortKey)
      const v = typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y), 'de', { sensitivity: 'base', numeric: true })
      return colState.sortDir === 'asc' ? v : -v
    })
    if (colState.group === 'none') return [{ key: '', titel: '', zeilen }]
    const m = new Map<string, OverheadLine[]>()
    for (const z of zeilen) {
      const k = colState.group === 'category' ? z.categoryName : (z.month ?? '')
      m.set(k, [...(m.get(k) ?? []), z])
    }
    return [...m.entries()]
      .sort(([a], [b]) => (colState.group === 'month' ? b.localeCompare(a) : a.localeCompare(b, 'de')))
      .map(([key, zeilen]) => ({ key, titel: colState.group === 'month' ? monatText(key || null) : key, zeilen }))
  }, [bericht, colState.sortKey, colState.sortDir, colState.group])
  const summe = (zeilen: OverheadLine[]) => zeilen.filter((z) => !z.inApproval).reduce((s, z) => s + Math.round(z.amount * 100), 0) / 100

  const renderCell = (z: OverheadLine, col: ColDef): React.ReactNode => {
    switch (col.key) {
      case 'invoiceDate':
        return tagText(z.invoiceDate)
      case 'amount':
        return <span className={z.inApproval ? 'text-muted-foreground' : ''}>{geldText(z.amount)}</span>
      case 'state':
        return z.inApproval ? <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-xs text-amber-700 dark:text-amber-300">in Freigabe</span> : ''
      default:
        return (z[col.key] as string | null) ?? ''
    }
  }
  const pfeil = (key: string) =>
    colState.sortKey === key ? colState.sortDir === 'asc' ? <ArrowUp className="h-3 w-3 text-primary" /> : <ArrowDown className="h-3 w-3 text-primary" /> : <ArrowUpDown className="h-3 w-3 opacity-30" />
  const anzahl = bericht?.lines.length ?? 0

  return (
    <div className={pageWide}>
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Gemeinkosten</h1>
          <p className="mt-1 text-sm text-muted-foreground">Was der Betrieb ohne Projektbezug ausgibt — je Zeile einer freigegebenen Eingangsrechnung. Beträge netto; wo die Vorsteuer nicht abziehbar ist, mit Steuer.</p>
        </div>
        <div className="flex items-center gap-2">
          <select aria-label="Gruppierung" className="rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground" value={colState.group} onChange={(e) => updateColState((prev) => ({ ...prev, group: e.target.value as Gruppierung }))}>
            {GRUPPEN.map(([k, l]) => (
              <option key={k} value={k}>
                {l}
              </option>
            ))}
          </select>
          <ExportButton
            request={(format, orientation) => overheadExportRequest({ ...filterParams(filterValues), format, orientation, filterInfo: filterText(filterValues, rahmen) || undefined }, [{ key: 'number', label: 'Eingangsnummer' }, ...shownColumns.map((c) => ({ key: c.key, label: c.label }))])}
            mail={{ source: 'EXPORT_INCOMING_OVERHEAD', title: 'Gemeinkosten', subject: 'Gemeinkosten' }}
          />
        </div>
      </div>

      <FilterBar availableFields={filterFields} value={filterValues} onChange={handleFiltersChange} layoutKey="incoming_overhead" defaultFieldKeys={['from', 'to', 'categoryId']} defaultValues={{}} adresse={adresse} />

      <div className="relative overflow-hidden rounded-lg border border-border bg-card shadow-sm">
        <button
          type="button"
          onClick={() => setColumnConfigMode((v) => !v)}
          className={`absolute right-1.5 top-1.5 z-10 flex h-6 w-6 items-center justify-center rounded-md transition-colors ${columnConfigMode ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
          title="Spalten konfigurieren"
        >
          {columnConfigMode ? <Check size={14} /> : <Settings size={14} />}
        </button>
        <HelpPopover topic="columns" className="absolute right-8 top-1.5" />
        {columnConfigMode && (
          <div className="border-b border-border bg-muted/30 px-3 py-2 pr-16">
            <LayoutDefaultActions
              settingKey={LAYOUT}
              currentValue={colState}
              onReset={(value) => {
                const next = { ...vorgabe(), ...((value as Partial<ColumnState> | null) ?? {}) }
                markUiSettingApplied(LAYOUT, next)
                setColState(next)
              }}
            />
          </div>
        )}
        {error && <div className="m-4 rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/50">
              <tr>
                <th className="relative px-4 py-1.5 text-left font-medium text-foreground" style={colState.columnWidths.number ? { width: colState.columnWidths.number } : undefined}>
                  <button type="button" onClick={() => handleSort('number')} className="flex items-center gap-1 hover:text-primary">
                    Eingangsnummer {pfeil('number')}
                  </button>
                  <div className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40" onMouseDown={(e) => handleResizeStart(e, 'number')} />
                </th>
                {shownColumns.map((col) => (
                  <th
                    key={col.key}
                    className={`relative select-none px-4 py-1.5 font-medium text-foreground ${col.align === 'right' ? 'text-right' : 'text-left'} ${columnConfigMode ? 'cursor-grab' : ''} ${dragOverKey === col.key ? 'bg-primary/10 outline outline-2 outline-primary' : ''}`}
                    draggable={columnConfigMode}
                    onDragStart={columnConfigMode ? () => (dragKeyRef.current = col.key) : undefined}
                    onDragOver={
                      columnConfigMode
                        ? (e) => {
                            e.preventDefault()
                            setDragOverKey(col.key)
                          }
                        : undefined
                    }
                    onDrop={columnConfigMode ? () => handleDrop(col.key) : undefined}
                    onDragEnd={columnConfigMode ? () => setDragOverKey(null) : undefined}
                    style={colState.columnWidths[col.key] ? { width: colState.columnWidths[col.key] } : undefined}
                  >
                    <div className={`flex items-center gap-1 ${col.align === 'right' ? 'justify-end' : ''}`}>
                      <button type="button" onClick={() => handleSort(col.key)} className="flex items-center gap-1 hover:text-primary">
                        {col.label} {pfeil(col.key)}
                      </button>
                      {columnConfigMode && (
                        <button type="button" onClick={() => updateColState((prev) => ({ ...prev, columnKeys: prev.columnKeys.filter((k) => k !== col.key) }))} className="ml-1 text-muted-foreground hover:text-destructive" title="Spalte entfernen">
                          ✕
                        </button>
                      )}
                    </div>
                    <div className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40" onMouseDown={(e) => handleResizeStart(e, col.key)} />
                  </th>
                ))}
                {columnConfigMode && availableToAdd.length > 0 && (
                  <th className="px-2 py-1.5">
                    <select
                      className="rounded border border-border bg-background px-1 py-0.5 text-xs text-foreground"
                      defaultValue=""
                      onChange={(e) => {
                        const key = e.target.value
                        if (!key) return
                        updateColState((prev) => ({ ...prev, columnKeys: [...prev.columnKeys, key] }))
                        e.target.value = ''
                      }}
                    >
                      <option value="">+ Spalte</option>
                      {availableToAdd.map((c) => (
                        <option key={c.key} value={c.key}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </th>
                )}
                <th className="w-4" />
              </tr>
            </thead>
            <tbody>
              {anzahl === 0 && (
                <tr>
                  <td colSpan={totalCols} className="px-4 py-8 text-center text-sm text-muted-foreground">
                    {loading ? 'Laden…' : 'Keine Gemeinkosten im gewählten Zeitraum.'}
                  </td>
                </tr>
              )}
              {gruppen.map((g) => (
                <Fragment key={g.key}>
                  {colState.group !== 'none' && (
                    <tr className="border-b border-border bg-muted/40 font-medium">
                      <td className="whitespace-nowrap px-4 py-1.5" colSpan={1}>
                        {g.titel || '—'}
                      </td>
                      {shownColumns.map((col) => (
                        <td key={col.key} className={`px-4 py-1.5 ${col.align === 'right' ? 'text-right tabular-nums' : ''}`}>
                          {col.key === 'amount' ? geldText(summe(g.zeilen)) : ''}
                        </td>
                      ))}
                      <td colSpan={totalCols - 1 - shownColumns.length} />
                    </tr>
                  )}
                  {g.zeilen.map((z, idx) => (
                    <tr key={z.id} onClick={() => navigate(`/incoming-invoices/${z.invoiceId}`)} className={`cursor-pointer border-b border-border last:border-0 ${idx % 2 === 0 ? 'bg-background' : 'bg-muted/20'} transition-colors hover:bg-accent/30`}>
                      <td className="whitespace-nowrap px-4 py-1.5 tabular-nums">{z.number ?? ''}</td>
                      {shownColumns.map((col) => (
                        <td key={col.key} className={`whitespace-nowrap px-4 py-1.5 ${col.align === 'right' ? 'text-right tabular-nums' : ''}`}>
                          {renderCell(z, col)}
                        </td>
                      ))}
                      <td colSpan={totalCols - 1 - shownColumns.length} />
                    </tr>
                  ))}
                </Fragment>
              ))}
              {anzahl > 0 && bericht && (
                <tr className="border-t-2 border-border bg-muted/50 font-medium">
                  <td className="whitespace-nowrap px-4 py-1.5 text-muted-foreground">{anzahl} Einträge</td>
                  {shownColumns.map((col) => (
                    <td key={col.key} className={`px-4 py-1.5 ${col.align === 'right' ? 'text-right tabular-nums' : ''}`}>
                      {col.key === 'amount' ? geldText(bericht.total) : ''}
                    </td>
                  ))}
                  <td colSpan={totalCols - 1 - shownColumns.length} />
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {bericht && bericht.inApproval > 0 && <p className="border-t border-border px-4 py-2 text-sm text-muted-foreground">Dazu {geldText(bericht.inApproval)} in Freigabe — zählt noch nicht.</p>}
      </div>
    </div>
  )
}
